import { Query } from 'redux-little-router';

import { Action } from '../../actions';
import { ADD_NEW_INTERNAL_BANNER } from '../../actions/bannersEdit/internal/addNewInternalBanner';
import { CLEAR_INTERNAL_BANNER } from '../../actions/bannersEdit/internal/clearInternalBanner';
import { DELETE_INTERNAL_BANNERS } from '../../actions/bannersEdit/internal/deleteInternalBanners';
import { DUPLICATE_INTERNAL_BANNER } from '../../actions/bannersEdit/internal/duplicateInternalBanner';
import { EDIT_INTERNAL_BANNER } from '../../actions/bannersEdit/internal/editInternalBanner';
import { OPEN_INTERNAL_BANNERS_EDIT } from '../../actions/bannersEdit/internal/openInternalBannersEdit';
import { RESTORE_INTERNAL_BANNERS } from '../../actions/bannersEdit/internal/restoreInternalBanners';
import { SAVE_INTERNAL_BANNERS } from '../../actions/bannersEdit/internal/saveInternalBanners';
import { SAVE_INTERNAL_BANNERS_SUCCESS } from '../../actions/bannersEdit/internal/saveInternalBannersSuccess';
import { SHOW_INTERNAL_BANNER_EDIT_REQUEST_ERROR } from '../../actions/bannersEdit/internal/showInternalBannerEditRequestError';
import { UPDATE_INTERNAL_BANNER_DESCRIPTION } from '../../actions/bannersEdit/internal/updateInternalBannerDescription';
import { UPDATE_INTERNAL_BANNERS_FOR_EDIT } from '../../actions/bannersEdit/internal/updateInternalBannersForEdit';
import { UPDATE_INTERNAL_BANNER_TEMPLATE_ID } from '../../actions/bannersEdit/internal/updateInternalBannerTemplateId';
import { UPDATE_INTERNAL_BANNER_TEMPLATE_RESOURCES } from '../../actions/bannersEdit/internal/updateInternalBannerTemplateResources';
import { BannersIdsForBannersEditHelpers } from '../../service/BannersIdsForBannersEditHelpers';
import { BannersScreenUrlSearchQueryParams } from '../../typings/banners';
import { PageStatus } from '../../typings/common';
import { CurrencyCode } from '../../typings/graphql-grid';
import { EditInternalBannersState } from '../../typings/internalBannersEditState';
import { State } from '../../typings/state';

import { addNewInternalBanner } from './internal/addNewInternalBanner';
import { clearInternalBanner } from './internal/clearInternalBanner';
import { deleteInternalBanners } from './internal/deleteInternalBanners';
import { duplicateInternalBanner } from './internal/duplicateInternalBanner';
import { editInternalBanner } from './internal/editInternalBanner';
import { openInternalBannersEdit } from './internal/openInternalBannersEdit';
import { restoreInternalBanners } from './internal/restoreInternalBanners';
import { saveInternalBanners } from './internal/saveInternalBanners';
import { saveInternalBannersSuccess } from './internal/saveInternalBannersSuccess';
import { showInternalBannerEditRequestError } from './internal/showInternalBannerEditRequestError';
import { updateInternalBannerDescription } from './internal/updateInternalBannerDescription';
import { updateInternalBannersForEdit } from './internal/updateInternalBannersForEdit';
import { updateInternalBannerTemplateId } from './internal/updateInternalBannerTemplateId';
import { updateInternalBannerTemplateResources } from './internal/updateInternalBannerTemplateResources';

interface EditInternalBannerPageLocalAndUrlData {
    campaignsIds: string[];
    bannersIds: string[];
    groupsIds: string[];
    isNew: boolean;
}

export const getEditInternalBannerPageData = (queryParams: Query): EditInternalBannerPageLocalAndUrlData => {
    const editBannersLocalData = BannersIdsForBannersEditHelpers.getBannersEditSessionValue(queryParams.ulogin);

    const editBannersDataFromUrl = {
        campaignsIds: queryParams[BannersScreenUrlSearchQueryParams.campaignsIds]
            ? queryParams[BannersScreenUrlSearchQueryParams.campaignsIds].split(',')
            : [],
        bannersIds: queryParams[BannersScreenUrlSearchQueryParams.bannersIds]
            ? queryParams[BannersScreenUrlSearchQueryParams.bannersIds].split(',')
            : [],
        groupsIds: queryParams[BannersScreenUrlSearchQueryParams.groupsIds]
            ? queryParams[BannersScreenUrlSearchQueryParams.groupsIds].split(',')
            : [],
        isNew: queryParams[BannersScreenUrlSearchQueryParams.isNew]
            ? Boolean(+queryParams[BannersScreenUrlSearchQueryParams.isNew])
            : false
    };

    const hasNotEmptyBannersDataFromUrl =
        editBannersDataFromUrl.campaignsIds.length !== 0 ||
        editBannersDataFromUrl.groupsIds.length !== 0 ||
        editBannersDataFromUrl.bannersIds.length !== 0;

    return hasNotEmptyBannersDataFromUrl ? editBannersDataFromUrl : editBannersLocalData;
};

const getEditInternalBannersInitialState = (): EditInternalBannersState => ({
    banners: {},
    bannersServerValues: {},
    bannersDraft: {},
    bannersIds: [],
    campaignsIds: [],
    groupsIds: [],
    isNew: false,
    pageStatus: PageStatus.LOADING,
    groupBannersList: {},
    clientInfo: {
        isBannerUpdateAllowed: false,
        isCpmBannerUpdateAllowed: false,
        isBannerPricesOnGridEnabled: false,
        isTurboForSerpWizardAllowed: false,
        isTycoonOrganizationsEnabled: false,
        isShowAutoAssignedOrganizationsEnabled: false,
        clientId: '',
        workCurrency: CurrencyCode.RUB
    },
    errors: [],
    placeTemplates: {},
    templateResources: {}
});

export function internalBannersMultiEdit(
    state: EditInternalBannersState = getEditInternalBannersInitialState(),
    action: Action,
    fullState: State
): EditInternalBannersState {
    switch (action.type) {
        case OPEN_INTERNAL_BANNERS_EDIT:
            return openInternalBannersEdit(state, action);

        case DELETE_INTERNAL_BANNERS:
            return deleteInternalBanners(state, action);

        case CLEAR_INTERNAL_BANNER:
            return clearInternalBanner(state, action);

        case RESTORE_INTERNAL_BANNERS:
            return restoreInternalBanners(state, action);

        case DUPLICATE_INTERNAL_BANNER:
            return duplicateInternalBanner(state, action, fullState);

        case UPDATE_INTERNAL_BANNERS_FOR_EDIT:
            return updateInternalBannersForEdit(state, action);

        case UPDATE_INTERNAL_BANNER_TEMPLATE_ID:
            return updateInternalBannerTemplateId(state, action);

        case UPDATE_INTERNAL_BANNER_DESCRIPTION:
            return updateInternalBannerDescription(state, action);

        case UPDATE_INTERNAL_BANNER_TEMPLATE_RESOURCES:
            return updateInternalBannerTemplateResources(state, action);

        case EDIT_INTERNAL_BANNER:
            return editInternalBanner(state, action);

        case ADD_NEW_INTERNAL_BANNER:
            return addNewInternalBanner(state, action);

        case SAVE_INTERNAL_BANNERS:
            return saveInternalBanners(state, action);

        case SAVE_INTERNAL_BANNERS_SUCCESS:
            return saveInternalBannersSuccess(state, action);

        case SHOW_INTERNAL_BANNER_EDIT_REQUEST_ERROR:
            return showInternalBannerEditRequestError(state, action);

        default:
            return state;
    }
}
